package com.fantasytenis.web.components.lineup

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.PlayerSkillCosts
import com.fantasytenis.shared.domain.skills.entity.SkillTier
import com.fantasytenis.shared.domain.tennis.entity.PlayerCosts
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.skills.RadarChart
import com.fantasytenis.web.components.skills.SkillBar
import com.fantasytenis.web.components.skills.SkillBarSize
import com.fantasytenis.web.components.skills.getCategoryColor
import com.fantasytenis.web.components.skills.getTierEmoji
import com.fantasytenis.web.hooks.isMobile
import com.fantasytenis.web.hooks.useResizablePanel
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.utils.WebPreferences
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.KeyboardEvent

/**
 * Convert PlayerCosts (from TennisEntities) to PlayerSkillCosts (for skill components)
 */
private fun PlayerCosts.toSkillCosts(): PlayerSkillCosts {
    return PlayerSkillCosts(
        serve = this.serve,
        forehand = this.forehand,
        backhand = this.backhand,
        volleys = this.volleys,
        fighting = this.fighting
    )
}

/**
 * Format match time from ISO string to readable format
 */
private fun formatMatchTime(isoTime: String?): String {
    if (isoTime == null) return ""
    return try {
        if (isoTime.contains("T")) {
            val timePart = isoTime.substringAfter("T").substringBefore("Z").substringBefore("+")
            val hourMinute = timePart.split(":").take(2)
            if (hourMinute.size >= 2) {
                val hour = hourMinute[0].toIntOrNull() ?: return isoTime
                val minute = hourMinute[1]
                val amPm = if (hour >= 12) "PM" else "AM"
                val hour12 = when {
                    hour == 0 -> 12
                    hour > 12 -> hour - 12
                    else -> hour
                }
                "$hour12:$minute $amPm"
            } else {
                isoTime
            }
        } else {
            isoTime
        }
    } catch (e: Exception) {
        isoTime
    }
}

/**
 * Format opponent name to be more compact (first initial + last name)
 */
private fun formatOpponentName(name: String): String {
    val parts = name.split(" ")
    return if (parts.size >= 2) {
        "${parts.first().first()}. ${parts.drop(1).joinToString(" ")}"
    } else {
        name
    }
}

/**
 * Player Skill Drawer Component
 *
 * A slide-in drawer that shows detailed skill information for a player,
 * allowing users to make informed decisions when building their lineup.
 *
 * @param player The player to display skill details for
 * @param selectingCategory The category currently being selected for (null if in browse mode)
 * @param remainingBudget The remaining salary budget
 * @param isAlreadyInLineup Whether this player is already in the lineup
 * @param onSelect Called when the user selects this player
 * @param onClose Called when the drawer should be closed
 */
@Composable
fun PlayerSkillDrawer(
    player: TennisPlayerEntity,
    selectingCategory: Category?,
    remainingBudget: Int,
    isAlreadyInLineup: Boolean,
    onSelect: () -> Unit,
    onClose: () -> Unit
) {
    val isMobileView = isMobile()
    val colors = Theme.colors
    val skillCosts = player.costs.toSkillCosts()
    val costForCategory = selectingCategory?.let { player.getCostForCategory(it) } ?: 0
    val canAfford = costForCategory <= remainingBudget
    val canSelect = selectingCategory != null && !isAlreadyInLineup && canAfford

    // Load saved width from preferences (desktop only)
    val savedWidth = remember { WebPreferences.getSkillDrawerWidth() }

    // Resize functionality (desktop/tablet only)
    val resizeHandlers = if (!isMobileView) {
        useResizablePanel(
            initialWidth = savedWidth,
            minWidth = 320,
            maxWidth = 600,
            onWidthChange = { newWidth ->
                WebPreferences.setSkillDrawerWidth(newWidth)
            }
        )
    } else null

    val panelWidth = if (isMobileView) 100.percent else resizeHandlers?.state?.width?.px ?: 420.px
    val isResizing = resizeHandlers?.state?.isResizing == true

    // Handle Escape key to close drawer
    DisposableEffect(Unit) {
        val handleKeyDown: (org.w3c.dom.events.Event) -> Unit = { event ->
            val keyEvent = event as? KeyboardEvent
            if (keyEvent?.key == "Escape") {
                onClose()
            }
        }
        document.addEventListener("keydown", handleKeyDown)

        // Prevent body scroll
        document.body?.style?.setProperty("overflow", "hidden")

        onDispose {
            document.removeEventListener("keydown", handleKeyDown)
            document.body?.style?.setProperty("overflow", "")
        }
    }

    // Modal backdrop
    Div({
        classes("skill-drawer-backdrop")
        attr("role", "dialog")
        attr("aria-modal", "true")
        attr("aria-label", "Player skill details")
        onClick { onClose() }
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(if (isMobileView) JustifyContent.Center else JustifyContent.FlexEnd)
            alignItems(if (isMobileView) AlignItems.FlexEnd else AlignItems.Stretch)
            property("z-index", "1000")
        }
    }) {
        // Drawer content
        Div({
            classes("skill-drawer-content")
            if (isResizing) classes("is-resizing")
            onClick { it.stopPropagation() }
            style {
                backgroundColor(colors.surface)
                width(panelWidth)
                property("max-height", if (isMobileView) "90vh" else "var(--app-height)")
                overflow("auto")
                property("-webkit-overflow-scrolling", "touch")
                if (isMobileView) {
                    borderRadius(16.px, 16.px, 0.px, 0.px)
                } else {
                    property("box-shadow", "-4px 0 20px rgba(0, 0, 0, 0.15)")
                }
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                position(Position.Relative)
            }
        }) {
            // Resize handle (desktop/tablet only)
            if (!isMobileView && resizeHandlers != null) {
                ResizeHandle(
                    isHovering = resizeHandlers.state.isHovering,
                    isResizing = resizeHandlers.state.isResizing,
                    onMouseDown = resizeHandlers.onMouseDown,
                    onTouchStart = resizeHandlers.onTouchStart,
                    onMouseEnter = resizeHandlers.onMouseEnter,
                    onMouseLeave = resizeHandlers.onMouseLeave
                )
            }
            // Header with close button
            Div({
                style {
                    padding(16.px, 20.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.FlexStart)
                    borderBottom(1.px, LineStyle.Solid, Colors.Border)
                    position(Position.Sticky)
                    top(0.px)
                    backgroundColor(Colors.Surface)
                    property("z-index", "1")
                }
            }) {
                // Player info
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(4.px)
                    }
                }) {
                    // Ranking + Name row
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(8.px)
                        }
                    }) {
                        // Ranking badge
                        Span({
                            style {
                                backgroundColor(Colors.Primary)
                                color(Colors.White)
                                padding(2.px, 8.px)
                                borderRadius(4.px)
                                fontSize(12.px)
                                fontWeight(700)
                            }
                        }) {
                            Text("#${player.atpRanking ?: "NR"}")
                        }

                        // Player name
                        Span({
                            style {
                                fontSize(18.px)
                                fontWeight(600)
                                color(Colors.TextPrimary)
                            }
                        }) {
                            Text(player.displayName)
                        }
                    }

                    // Country
                    Span({
                        style {
                            fontSize(14.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text(player.country)
                    }
                }

                // Close button - 44px touch target for mobile accessibility
                Span({
                    style {
                        width(44.px)
                        height(44.px)
                        minWidth(44.px)
                        borderRadius(50.percent)
                        backgroundColor(Colors.Gray100)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        fontSize(20.px)
                        color(Colors.TextSecondary)
                        cursor("pointer")
                        flexShrink(0)
                    }
                    onClick { onClose() }
                    title("Close")
                }) {
                    Text("\u00D7")
                }
            }

            // Scrollable content
            Div({
                style {
                    flex(1)
                    overflow("auto")
                    padding(20.px)
                }
            }) {
                // Radar chart section
                Section({
                    style {
                        marginBottom(24.px)
                    }
                }) {
                    H4({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(Colors.TextPrimary)
                            marginBottom(16.px)
                            textAlign("center")
                        }
                    }) {
                        Text("Skill Profile")
                    }

                    // Center the radar chart
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            marginBottom(16.px)
                        }
                    }) {
                        RadarChart(costs = skillCosts, size = 180, showLabels = true)
                    }

                    // Summary stats
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            gap(32.px)
                        }
                    }) {
                        StatItem(label = "Total", value = "${skillCosts.total}")
                        StatItem(label = "Average", value = skillCosts.average.asDynamic().toFixed(1) as String)
                    }
                }

                // Skill breakdown section
                Section({
                    style {
                        marginBottom(24.px)
                    }
                }) {
                    H4({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(Colors.TextPrimary)
                            marginBottom(16.px)
                        }
                    }) {
                        Text("Skill Breakdown")
                    }

                    Category.entries.forEach { category ->
                        val cost = skillCosts.getCostForCategory(category)
                        val tier = SkillTier.fromCost(cost)
                        val isSelected = category == selectingCategory

                        Div({
                            style {
                                marginBottom(12.px)
                                if (isSelected) {
                                    backgroundColor(Color("rgba(0, 166, 81, 0.08)"))
                                    padding(8.px)
                                    marginLeft((-8).px)
                                    marginRight((-8).px)
                                    borderRadius(8.px)
                                }
                            }
                        }) {
                            // Category header with tier
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    justifyContent(JustifyContent.SpaceBetween)
                                    alignItems(AlignItems.Center)
                                    marginBottom(6.px)
                                }
                            }) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(8.px)
                                    }
                                }) {
                                    Span({
                                        style {
                                            fontSize(13.px)
                                            fontWeight(600)
                                            color(getCategoryColor(category))
                                        }
                                    }) {
                                        Text(category.displayName)
                                    }
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            color(Colors.TextSecondary)
                                        }
                                    }) {
                                        Text("${getTierEmoji(tier)} ${tier.label}")
                                    }
                                }

                                if (isSelected) {
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            fontWeight(600)
                                            color(Colors.Primary)
                                            backgroundColor(Colors.PrimaryLight)
                                            padding(2.px, 6.px)
                                            borderRadius(4.px)
                                        }
                                    }) {
                                        Text("SELECTING")
                                    }
                                }
                            }

                            // Skill bar
                            SkillBar(
                                category = category,
                                value = cost,
                                showTier = false,
                                showLabel = false,
                                size = SkillBarSize.MEDIUM
                            )
                        }
                    }
                }

                // Opponent matchup section (if opponent assigned)
                if (player.opponentName != null) {
                    Section({
                        style {
                            marginBottom(24.px)
                            padding(16.px)
                            backgroundColor(Colors.Gray100)
                            borderRadius(8.px)
                        }
                    }) {
                        H4({
                            style {
                                fontSize(12.px)
                                fontWeight(600)
                                color(Colors.TextSecondary)
                                marginBottom(8.px)
                                textTransform("uppercase")
                                letterSpacing(0.5.px)
                            }
                        }) {
                            Text("Upcoming Match")
                        }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(12.px)
                                flexWrap(FlexWrap.Wrap)
                            }
                        }) {
                            Span({
                                style {
                                    fontSize(14.px)
                                    color(Colors.TextPrimary)
                                }
                            }) {
                                Text("vs ${formatOpponentName(player.opponentName!!)}")
                            }

                            // Matchup badge
                            if (player.atpRanking != null && player.opponentRanking != null) {
                                MatchupIndicator(
                                    playerRanking = player.atpRanking,
                                    opponentRanking = player.opponentRanking,
                                    showLabel = true,
                                    compact = false
                                )
                            }

                            // Match time
                            if (player.matchTime != null) {
                                Span({
                                    style {
                                        fontSize(13.px)
                                        color(Colors.TextSecondary)
                                    }
                                }) {
                                    Text(formatMatchTime(player.matchTime))
                                }
                            }
                        }
                    }
                }
            }

            // Footer with action buttons
            Div({
                style {
                    padding(16.px, 20.px)
                    borderTop(1.px, LineStyle.Solid, Colors.Border)
                    backgroundColor(Colors.Surface)
                }
            }) {
                // Status badges for disabled states
                if (isAlreadyInLineup) {
                    Div({
                        style {
                            backgroundColor(Color("#fef3c7"))
                            color(Color("#92400e"))
                            padding(10.px, 16.px)
                            borderRadius(8.px)
                            fontSize(14.px)
                            fontWeight(600)
                            textAlign("center")
                            marginBottom(12.px)
                        }
                    }) {
                        Text("Already in Lineup")
                    }
                } else if (selectingCategory != null && !canAfford) {
                    Div({
                        style {
                            backgroundColor(Color("#fee2e2"))
                            color(Color("#991b1b"))
                            padding(10.px, 16.px)
                            borderRadius(8.px)
                            fontSize(14.px)
                            fontWeight(600)
                            textAlign("center")
                            marginBottom(12.px)
                        }
                    }) {
                        Text("Over Budget (\$${costForCategory}K needed, \$${remainingBudget}K remaining)")
                    }
                }

                // Select button (only show when in selecting mode)
                if (selectingCategory != null) {
                    val categoryColor = getCategoryColor(selectingCategory)

                    Div({
                        style {
                            backgroundColor(if (canSelect) categoryColor else Colors.Gray300)
                            color(Colors.White)
                            padding(14.px, 24.px)
                            borderRadius(8.px)
                            fontSize(16.px)
                            fontWeight(600)
                            textAlign("center")
                            cursor(if (canSelect) "pointer" else "not-allowed")
                            marginBottom(12.px)
                            property("transition", "background-color 0.2s")
                        }
                        if (canSelect) {
                            onClick { onSelect() }
                        }
                    }) {
                        Text("SELECT FOR \$${costForCategory}K (${selectingCategory.displayName})")
                    }
                }

                // View full profile link
                Div({
                    style {
                        textAlign("center")
                    }
                }) {
                    Span({
                        style {
                            fontSize(13.px)
                            color(Colors.Primary)
                            cursor("pointer")
                        }
                        onClick {
                            onClose()
                            NavigationState.navigate(Route.PlayerSkillDetail(player.id))
                        }
                    }) {
                        Text("View Full Profile \u2192")
                    }
                }
            }
        }
    }
}

/**
 * Stat item component for total/average display
 */
@Composable
private fun StatItem(label: String, value: String) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(20.px)
                fontWeight(700)
                color(Colors.Primary)
            }
        }) {
            Text(value)
        }
        Div({
            style {
                fontSize(12.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text(label)
        }
    }
}

/**
 * Style extension for text-transform
 */
private fun StyleScope.textTransform(value: String) {
    property("text-transform", value)
}

/**
 * Style extension for letter-spacing
 */
private fun StyleScope.letterSpacing(value: CSSNumeric) {
    property("letter-spacing", value.toString())
}

/**
 * Style extension for border-bottom shorthand
 */
private fun StyleScope.borderBottom(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-bottom", "$width $style $color")
}

/**
 * Style extension for border-top shorthand
 */
private fun StyleScope.borderTop(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-top", "$width $style $color")
}

/**
 * Resize handle component for the drawer's left edge.
 *
 * Shows a visual indicator on hover and during drag.
 */
@Composable
private fun ResizeHandle(
    isHovering: Boolean,
    isResizing: Boolean,
    onMouseDown: (org.w3c.dom.events.Event) -> Unit,
    onTouchStart: (org.w3c.dom.events.Event) -> Unit,
    onMouseEnter: () -> Unit,
    onMouseLeave: () -> Unit
) {
    val colors = Theme.colors
    val showIndicator = isHovering || isResizing

    Div({
        classes("resize-handle")
        style {
            position(Position.Absolute)
            left(0.px)
            top(0.px)
            bottom(0.px)
            width(12.px)
            cursor("ew-resize")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            backgroundColor(
                when {
                    isResizing -> Color("rgba(0, 166, 81, 0.3)")
                    isHovering -> Color("rgba(0, 166, 81, 0.15)")
                    else -> Color.transparent
                }
            )
            property("transition", "background-color 0.15s ease")
            property("z-index", "2")
            // Subtle left border as hint
            if (!showIndicator) {
                property("border-left", "1px solid ${colors.border}")
            }
        }
        onMouseDown { onMouseDown(it.nativeEvent) }
        onTouchStart { onTouchStart(it.nativeEvent) }
        onMouseEnter { onMouseEnter() }
        onMouseLeave { onMouseLeave() }
    }) {
        // Grip indicator (3 vertical dots)
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(4.px)
                opacity(if (showIndicator) 1.0 else 0.0)
                property("transition", "opacity 0.15s ease")
            }
        }) {
            repeat(3) {
                Div({
                    style {
                        width(4.px)
                        height(4.px)
                        borderRadius(2.px)
                        backgroundColor(colors.primary)
                    }
                })
            }
        }
    }
}
